import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  WatermarkStage,
  WMAbnormal,
  WMAtm,
  WMLoan,
  WMParking,
  WMProfit,
} from "../components/styled/WaterMark";
import { useLocalization } from "../hooks/useLocalization";

/** ====== 外層容器：用 flex 垂直排版 ====== */
const Page = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 1024px;
  margin: 0 auto;
  z-index: 10; /* 蓋在 layout 背景上 */
`;

/** ====== 上方文案區（副標、主標、CTA） ====== */
const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  margin-top: 1.5rem;
  z-index: 20; /* 高於底部所有浮水印與圖片 */
`;

const SubTitle = styled.div`
  font-family: "PingFang TC";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  text-align: center;
  letter-spacing: 3px;

  background-clip: text;
  color: transparent;
  background: linear-gradient(90deg, #a3a9c4 0%, #f2f3f7 51.44%, #8287a0 100%);
  -webkit-background-clip: text;

  z-index: 20;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 24px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-family: "PingFang TC";
  font-style: normal;
  font-weight: 600;
  font-size: 38px;
  text-align: center;
  letter-spacing: 0.02em;

  background: linear-gradient(
    90deg,
    #757ea7 -8.1%,
    #ffffff 53.63%,
    #3e466d 108.63%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;

  z-index: 20;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 80px;
  }
`;

const CtaBtn = styled.button`
  margin-top: 8px;
  padding: 10px 40px;
  box-sizing: border-box;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 1px;
  line-height: 1;

  border: none;
  background: linear-gradient(360deg, #0c34f8 0%, #90b3ff 100%);
  box-shadow: 0px 0px 32.8px #134eff, 0px 4px 6.1px rgba(0, 0, 0, 0.25);
  border-radius: 110px;

  transition: filter 0.15s ease, transform 0.1s ease;
  &:hover {
    filter: brightness(1.07);
  }
  &:active {
    transform: scale(0.99);
  }
  cursor: pointer;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 32px;
    min-height: 80px;
    min-width: 240px;
  }
`;

/** ====== VS 對比區域 ====== */
const VsContainer = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: env(safe-area-inset-bottom, 0px);
  height: 50vh;
  z-index: 15;
  pointer-events: none;
  overflow: hidden;
`;

const VsContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const PersonImage = styled.img<{ $side: "left" | "right" }>`
  position: absolute;
  bottom: 0;
  height: 50vh;
  width: auto;
  object-fit: cover;
  user-select: none;
  pointer-events: none;

  ${(props) =>
    props.$side === "left"
      ? `
    left: 0;
    object-position: left bottom;
    transform: translateX(-45%);
  `
      : `
    right: 0;
    object-position: right bottom;
    transform: translateX(45%);
  `}
`;

const VsCenter = styled.div`
  position: absolute;
  left: 50%;
  top: 10%;
  transform: translate(-50%, 0%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 16;
`;

const VsText = styled.div`
  font-family: "PingFang TC", "SF Pro TC", "Noto Sans TC", system-ui, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 110px;
  line-height: 1;
  letter-spacing: 5px;
  text-align: center;

  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(255, 255, 255, 0.6) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;

  text-shadow: 0px 0px 40px rgba(255, 255, 255, 0.6);

  /* 針對手機尺寸 */
  @media (max-width: 767px) {
    font-size: 80px;
  }

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 160px;
  }
`;

const VsSubText = styled.div`
  font-family: "PingFang TC";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  letter-spacing: 1px;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);

  /* 針對手機尺寸 */
  @media (max-width: 767px) {
    font-size: 12px;
  }

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 20px;
  }
`;

export default function CountyMagistratePage() {
  const navigate = useNavigate();
  const { t } = useLocalization();

  const handleStartChallenge = () => {
    navigate("/pick-scam");
  };

  return (
    <Page>
      {/* 響應式背景浮水印：整張舞台等比縮放 */}
      <WatermarkStage>
        <WMLoan>{t("home.loanAd")}</WMLoan>
        <WMParking>{t("home.parkingFee")}</WMParking>
        <WMProfit>{t("home.investmentAd")}</WMProfit>
        <WMAbnormal>{t("home.accountAbnormal")}</WMAbnormal>
        <WMAtm>{t("home.atmInstruction")}</WMAtm>
      </WatermarkStage>

      {/* 上方文案 */}
      <HeroCopy>
        <SubTitle>揭開詐騙訊息偽裝術</SubTitle>
        <Title>真假縣長比一比</Title>
        <CtaBtn onClick={handleStartChallenge}>開始挑戰</CtaBtn>
      </HeroCopy>

      {/* VS 對比區域 */}
      <VsContainer>
        <VsContent>
          {/* 左側：詐騙者 */}
          <PersonImage $side="left" src="/images/hacker.png" alt="Scam" />

          {/* 中間：VS 文字 */}
          <VsCenter>
            <VsText>VS</VsText>
            <VsSubText>SCAM VS OFFICIAL</VsSubText>
          </VsCenter>

          {/* 右側：縣長 */}
          <PersonImage $side="right" src="/images/li-shan.png" alt="Official" />
        </VsContent>
      </VsContainer>
    </Page>
  );
}
